import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { Eye, EyeOff, AlertCircle } from 'lucide-react';
import { validateEmail } from '../../../utils/turkishValidation';
import { supabase } from '../../../lib/supabase';

const LoginForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [serverError, setServerError] = useState('');
  const [errorDetails, setErrorDetails] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [isEmailNotConfirmed, setIsEmailNotConfirmed] = useState(false);
  const [resendStatus, setResendStatus] = useState(''); // 'sending' | 'sent' | 'error'

  useEffect(() => {
    if (location?.state?.message) {
      setSuccessMessage(location?.state?.message);
      if (location?.state?.email) {
        setFormData((prev) => ({ ...prev, email: location?.state?.email }));
      }
    }
  }, [location?.state]);

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
    if (errors?.[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    const emailError = validateEmail(formData?.email);
    if (emailError) newErrors.email = emailError;

    if (!formData?.password?.trim()) {
      newErrors.password = 'Şifre gereklidir';
    } else if (formData?.password?.length < 6) {
      newErrors.password = 'Şifre en az 6 karakter olmalıdır';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setServerError('');
    setErrorDetails(null);
    setIsEmailNotConfirmed(false);
    setResendStatus('');

    try {
      const { data, error } = await signIn(formData?.email, formData?.password);

      if (error) {
        console.error('Login error:', error);

        if (error?.message?.includes('Email not confirmed')) {
          setServerError('Lütfen giriş yapmadan önce e-postanızı doğrulayın. Gelen kutunuzdaki doğrulama linkini kontrol edin.');
          setIsEmailNotConfirmed(true);
        } else {
          setServerError(getFriendlyAuthError(error));
        }
        setIsSubmitting(false);
        return;
      }

      if (data?.user) {
        navigate('/event-discovery');
      }
    } catch (err) {
      console.error('Login error:', err);
      const isInApp = isInAppBrowser();
      const inAppSuffix = isInApp
        ? ' Instagram/Facebook tarayıcısı girişleri engelliyor olabilir. Lütfen sağ üstteki menüden "Tarayıcıda Aç" (Safari/Chrome) seçeneğiyle devam edin.'
        : '';

      if (
        err?.name === 'AuthRetryableFetchError' || err?.message?.includes('fetch failed') ||
        err?.message?.includes('Failed to fetch') ||
        err?.message?.includes('503')
      ) {
        setServerError('Giriş sunucularına şu an ulaşılamıyor. Lütfen sayfanızı yenileyip tekrar deneyin.' + inAppSuffix);
      } else {
        setServerError('Beklenmeyen bir hata oluştu. Lütfen tekrar deneyin.' + inAppSuffix);
      }
      setIsSubmitting(false);
    }
  };

  const handleResendVerification = async () => {
    if (!formData?.email) return;
    setResendStatus('sending');
    try {
      const { error } = await supabase?.auth?.resend({
        type: 'signup',
        email: formData?.email,
      });
      if (error) {
        setResendStatus('error');
      } else {
        setResendStatus('sent');
      }
    } catch {
      setResendStatus('error');
    }
  };

  const copyErrorDetails = () => {
    if (errorDetails) {
      const errorText = JSON.stringify(errorDetails, null, 2);
      navigator.clipboard?.writeText(errorText);
    }
  };

  const isInAppBrowser = () => {
    const ua = navigator?.userAgent || '';
    return /Instagram|FBAV|FBAN|FB_IAB|FB4A|FBIOS|Facebook/i?.test(ua);
  };

  const getFriendlyAuthError = (error) => {
    const isInApp = isInAppBrowser();
    const inAppSuffix = isInApp
      ? ' Instagram/Facebook tarayıcısı girişleri engelliyor olabilir. Lütfen sağ üstteki menüden "Tarayıcıda Aç" (Safari/Chrome) seçeneğiyle devam edin.'
      : '';

    if (
      error?.name === 'AuthRetryableFetchError' ||
      error?.status === 503 ||
      error?.message?.includes('503') ||
      error?.message?.includes('fetch failed') ||
      error?.message?.includes('Failed to fetch')
    ) {
      return 'Giriş sunucularına şu an ulaşılamıyor. Lütfen sayfanızı yenileyip tekrar deneyin.' + inAppSuffix;
    }
    if (error?.message?.includes('yasaklanmıştır') || error?.message?.includes('banned')) {
      return 'Hesabınız yönetici tarafından yasaklanmıştır. Lütfen destek ekibiyle iletişime geçin.';
    }
    if (error?.message?.includes('Email not confirmed')) {
      return null; // handled separately
    }
    if (error?.message?.includes('Invalid login credentials') || error?.message?.includes('Invalid')) {
      return 'Geçersiz e-posta veya şifre. Lütfen tekrar deneyin.' + inAppSuffix;
    }
    if (error?.message?.includes('Email rate limit exceeded')) {
      return 'Çok fazla giriş denemesi yaptınız. Lütfen birkaç dakika sonra tekrar deneyin.';
    }
    if (error?.message?.includes('Network') || error?.message?.includes('network')) {
      return 'Bağlantı hatası. İnternet bağlantınızı kontrol edip tekrar deneyin.' + inAppSuffix;
    }
    return 'Giriş başarısız oldu. Lütfen tekrar deneyin.' + inAppSuffix;
  };

  return (
    <div className="w-full max-w-md mx-auto">
      {successMessage &&
      <div style={{
        padding: '12px 16px',
        backgroundColor: 'var(--color-success-light, #efe)',
        border: '1px solid var(--color-success)',
        borderRadius: '8px',
        color: 'var(--color-success)',
        fontSize: '0.875rem',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      }}>
          <Icon name="CheckCircle" size={16} />
          <span>{successMessage}</span>
        </div>
      }
      {serverError &&
      <div style={{
        padding: '12px 16px',
        backgroundColor: 'var(--color-error-light, #fee)',
        border: '1px solid var(--color-error)',
        borderRadius: '8px',
        color: 'var(--color-error)',
        fontSize: '0.875rem'
      }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
            <Icon name="AlertCircle" size={16} style={{ flexShrink: 0, marginTop: '2px' }} />
            <div style={{ flex: 1 }}>
              <div>{serverError}</div>
              {isEmailNotConfirmed &&
              <div style={{ marginTop: '10px' }}>
                {resendStatus === 'sent' ? (
                  <span style={{ fontSize: '0.8rem', color: 'var(--color-success, #16a34a)', fontWeight: 500 }}>
                    ✓ Doğrulama e-postası gönderildi. Lütfen gelen kutunuzu kontrol edin.
                  </span>
                ) : resendStatus === 'error' ? (
                  <span style={{ fontSize: '0.8rem' }}>
                    Gönderim başarısız oldu.{' '}
                    <button
                      type="button"
                      onClick={handleResendVerification}
                      style={{
                        fontSize: '0.8rem',
                        color: 'var(--color-error)',
                        textDecoration: 'underline',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: 0,
                        fontWeight: 600
                      }}>
                      Tekrar dene
                    </button>
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={handleResendVerification}
                    disabled={resendStatus === 'sending'}
                    style={{
                      fontSize: '0.8rem',
                      color: 'var(--color-error)',
                      textDecoration: 'underline',
                      background: 'none',
                      border: 'none',
                      cursor: resendStatus === 'sending' ? 'not-allowed' : 'pointer',
                      padding: 0,
                      fontWeight: 600,
                      opacity: resendStatus === 'sending' ? 0.6 : 1
                    }}>
                    {resendStatus === 'sending' ? 'Gönderiliyor...' : 'Doğrulama e-postasını tekrar gönder'}
                  </button>
                )}
              </div>
              }
            </div>
          </div>
        </div>
      }
      <form onSubmit={handleSubmit} className="space-y-6">
        <Input
          type="email"
          name="email"
          label="E-posta Adresi"
          placeholder="ornek@paddleturkiye.com"
          value={formData?.email}
          onChange={handleInputChange}
          error={errors?.email}
          required
          disabled={isSubmitting} />

        <div className="relative">
          <Input
            type={showPassword ? 'text' : 'password'}
            name="password"
            label="Şifre"
            placeholder="Şifrenizi giriniz"
            value={formData?.password}
            onChange={handleInputChange}
            error={errors?.password}
            required
            disabled={isSubmitting} />

          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-[38px] text-muted-foreground hover:text-foreground transition-smooth"
            aria-label={showPassword ? 'Şifreyi gizle' : 'Şifreyi göster'}>

            <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={20} />
          </button>
        </div>

        <div className="flex items-center justify-between text-sm">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e?.target?.checked)}
              className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500" />

            <span className="text-slate-600">Beni hatırla</span>
          </label>
          <Link
            to="/password-reset"
            className="text-blue-600 hover:text-blue-700 font-medium">

            Şifremi unuttum
          </Link>
        </div>

        <Button
          type="submit"
          variant="default"
          fullWidth
          loading={isSubmitting}
          disabled={isSubmitting}
          className="mt-6 md:mt-7 lg:mt-8">

          Giriş Yap
        </Button>
      </form>

      

































      <div className="text-center pt-4 md:pt-5 lg:pt-6">
        <p className="text-sm md:text-base text-muted-foreground">
          Hesabınız yok mu?{' '}
          <button
            type="button"
            onClick={() => navigate('/register')}
            className="text-primary hover:text-secondary transition-smooth font-semibold">

            Hemen Kayıt Olun
          </button>
        </p>
      </div>
    </div>);

};

export default LoginForm;